import { useState } from 'react'
import { Card, Table, Tag, Button, Tabs, Statistic, Row, Col, Space, Input, Switch } from 'antd'
import { ReloadOutlined, PlusOutlined, EyeOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons'
import type { ApiEndpoint, ApiKey } from '../../types/admin'
import { mockApiEndpoints, mockApiKeys } from '../../services/mockData'

const { TabPane } = Tabs

const ApiService = () => {
  const [endpoints] = useState<ApiEndpoint[]>(mockApiEndpoints)
  const [apiKeys] = useState<ApiKey[]>(mockApiKeys)
  const [activeTab, setActiveTab] = useState('endpoints')

  const endpointColumns = [
    {
      title: '接口名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      render: (method: string) => (
        <Tag color={method === 'GET' ? 'green' : method === 'POST' ? 'blue' : 'orange'}>
          {method}
        </Tag>
      ),
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      render: (text: string) => <code className="text-sm">{text}</code>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          online: 'success',
          offline: 'default',
          degraded: 'warning',
          maintenance: 'processing',
        }
        const texts: Record<string, string> = {
          online: '🟢 正常',
          offline: '🔴 离线',
          degraded: '🟡 降级',
          maintenance: '🔵 维护',
        }
        return <Tag color={colors[status]}>{texts[status]}</Tag>
      },
    },
    {
      title: 'QPS',
      key: 'qps',
      render: (_: any, record: ApiEndpoint) => (
        <span>{record.currentQps} / {record.maxQps}</span>
      ),
    },
    {
      title: '平均延迟',
      key: 'latency',
      render: (_: any, record: ApiEndpoint) => (
        <span>{record.avgLatency}ms</span>
      ),
    },
    {
      title: '错误率',
      key: 'errorRate',
      render: (_: any, record: ApiEndpoint) => (
        <span style={{ color: record.errorRate > 1 ? 'red' : 'inherit' }}>
          {record.errorRate}%
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: () => (
        <Space>
          <Button type="link">详情</Button>
          <Button type="link">配置</Button>
        </Space>
      ),
    },
  ]

  const apiKeyColumns = [
    {
      title: '密钥名称',
      dataIndex: 'keyName',
      key: 'keyName',
    },
    {
      title: '密钥ID',
      dataIndex: 'keyId',
      key: 'keyId',
      render: (text: string) => <code className="text-sm">{text}</code>,
    },
    {
      title: '权限',
      key: 'permissions',
      render: (_: any, record: ApiKey) => (
        <Space>
          {record.permissions.map(p => (
            <Tag key={p} color={p === 'admin' ? 'red' : p === 'write' ? 'blue' : 'green'}>
              {p}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '调用次数',
      key: 'calls',
      render: (_: any, record: ApiKey) => record.usageStats.totalCalls.toLocaleString(),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : status === 'expired' ? 'warning' : 'error'}>
          {status === 'active' ? '🟢 有效' : status === 'expired' ? '🟡 过期' : '🔴 吊销'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: () => (
        <Space>
          <Button type="link" icon={<EyeOutlined />}>查看</Button>
          <Button type="link" icon={<ReloadOutlined />}>刷新</Button>
          <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">🔌 数据接口服务管理</h1>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="接口列表" key="endpoints">
          {/* 统计卡片 */}
          <Row gutter={16} className="mb-6">
            <Col span={6}>
              <Card>
                <Statistic title="总接口数" value={endpoints.length} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="正常服务" 
                  value={endpoints.filter(e => e.status === 'online').length}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="异常服务" 
                  value={endpoints.filter(e => e.status !== 'online').length}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="总调用量" value="12.5K" suffix="/小时" />
              </Card>
            </Col>
          </Row>

          <Card
            title="📋 接口服务列表"
            extra={
              <Space>
                <Button icon={<ReloadOutlined />}>批量测试</Button>
                <Button type="primary" icon={<PlusOutlined />}>新增接口</Button>
              </Space>
            }
          >
            <Table
              columns={endpointColumns}
              dataSource={endpoints}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="调用监控" key="monitoring">
          <Row gutter={16} className="mb-6">
            <Col span={6}>
              <Card>
                <Statistic title="总调用量" value={12580} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="成功率" value={99.2} suffix="%" />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="平均响应" value={45} suffix="ms" />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="当前QPS" value={45} />
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Card title="📈 QPS趋势" className="h-80">
                <div className="h-full flex items-center justify-center text-gray-400">
                  [QPS趋势图]
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="📊 接口调用分布" className="h-80">
                <div className="h-full flex items-center justify-center text-gray-400">
                  [饼图: 各接口占比]
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="API密钥" key="keys">
          <Card
            title="🔑 API密钥管理"
            extra={
              <Button type="primary" icon={<KeyOutlined />}>
                生成新密钥
              </Button>
            }
          >
            <Table
              columns={apiKeyColumns}
              dataSource={apiKeys}
              rowKey="keyId"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="接口配置" key="config">
          <Card title="⚙️ 全局接口配置">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-3">速率限制</h4>
                <div className="flex items-center gap-4">
                  <span>默认QPS限制:</span>
                  <Input style={{ width: 100 }} defaultValue={100} />
                  <span>次/秒</span>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">超时配置</h4>
                <div className="flex items-center gap-4">
                  <span>默认超时:</span>
                  <Input style={{ width: 100 }} defaultValue={30} />
                  <span>秒</span>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">缓存配置</h4>
                <div className="flex items-center gap-4">
                  <Switch defaultChecked />
                  <span>启用接口缓存</span>
                  <span className="ml-4">默认缓存时间:</span>
                  <Input style={{ width: 100 }} defaultValue={300} />
                  <span>秒</span>
                </div>
              </div>
              <Button type="primary">保存配置</Button>
            </div>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  )
}

export default ApiService
